import { Component } from '@theme/component';
import { onAnimationEnd } from '@theme/utilities';
import { StandardEvents } from '@shopify/events';

class CartIcon extends Component {
  requiredRefs = [
    'cartBubble',
    'cartBubbleText',
    'cartBubbleCount'
  ];

  get currentCartCount() {
    const value = parseInt(
      this.refs.cartBubbleCount.textContent || '0',
      10
    );

    return Number.isNaN(value) ? 0 : value;
  }

  set currentCartCount(value) {
    const count = Math.max(0, Number(value) || 0);

    this.refs.cartBubbleCount.textContent =
      count < 100 ? String(count) : '99+';
  }

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(
      StandardEvents.cartLinesUpdate,
      this.onCartUpdate
    );

    window.addEventListener(
      'pageshow',
      this.onPageShow
    );

    /*
     * IMPORTANT:
     * Get the real cart directly from Shopify.
     */
    this.loadCartCount();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(
      StandardEvents.cartLinesUpdate,
      this.onCartUpdate
    );

    window.removeEventListener(
      'pageshow',
      this.onPageShow
    );
  }

  onPageShow = () => {
    this.loadCartCount();
  };

  /*
   * Get the real cart from Shopify.
   *
   * cart.items.length = number of different cart lines.
   *
   * Example:
   *
   * Shirt x10
   * Shoes x20
   *
   * cart.items.length = 2
   */
  loadCartCount = async () => {
    try {
      const response = await fetch('/cart.js', {
        method: 'GET',
        headers: {
          'Accept': 'application/json'
        },
        cache: 'no-store'
      });

      if (!response.ok) {
        throw new Error('Could not load cart.');
      }

      const cart = await response.json();

      const count = Array.isArray(cart.items)
        ? cart.items.length
        : 0;

      this.renderCartBubble(count, false);

    } catch (error) {
      console.warn(
        '[cart-icon] Could not load cart:',
        error
      );
    }
  };

  /*
   * Handle Shopify cart update events.
   *
   * Instead of trusting totalQuantity,
   * reload the actual cart.
   */
  onCartUpdate = () => {
    this.loadCartCount();
  };

  renderCartBubble = async (
    itemCount,
    animate = true
  ) => {
    const count = Math.max(
      0,
      Number(itemCount) || 0
    );

    /*
     * Show/hide the bubble.
     */
    this.refs.cartBubble.classList.toggle(
      'visually-hidden',
      count === 0
    );

    this.refs.cartBubbleCount.classList.toggle(
      'hidden',
      count === 0
    );

    /*
     * Put the actual number into the bubble.
     */
    this.currentCartCount = count;

    /*
     * Tell the cart icon it contains products.
     */
    this.classList.toggle(
      'header-actions__cart-icon--has-cart',
      count > 0
    );

    /*
     * Save the number.
     */
    sessionStorage.setItem(
      'cart-count',
      JSON.stringify({
        value: String(count),
        timestamp: Date.now()
      })
    );

    if (!animate || count === 0) {
      return;
    }

    await new Promise((resolve) =>
      requestAnimationFrame(resolve)
    );

    this.refs.cartBubble.classList.add(
      'cart-bubble--animating'
    );

    try {
      await onAnimationEnd(
        this.refs.cartBubbleText
      );
    } catch (_) {
      // Animation failure should not affect cart count.
    }

    this.refs.cartBubble.classList.remove(
      'cart-bubble--animating'
    );
  };
}

if (!customElements.get('cart-icon')) {
  customElements.define(
    'cart-icon',
    CartIcon
  );
}